import { Badge } from "../../layout/shared";
import React from "react";
import type { CSSProperties } from "styled-components/macro";
import styled from "styled-components/macro";
import { TextBaseMedium } from "../Typography/TypographyV2";

const ButtonGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
  background: ${({ theme }) => theme.resolve("color_bg_surface_standard")};
  border-radius: ${({ theme }) => theme.resolve("radius_md")};
  border: ${({ theme }) => theme.resolve("border_sm")} solid
    ${({ theme }) => theme.resolve("color_border_light")};
  overflow: hidden;
`;

const StyledButton = styled.button<{ isActive: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.resolve("spacing_sm")};
  padding: ${({ theme }) => theme.resolve("spacing_md")}
    ${({ theme }) => theme.resolve("spacing_xl")};
  border: none;
  background: ${({ isActive, theme }) =>
    isActive ? theme.resolve("color_bg_surface_subtle") : "transparent"};
  cursor: pointer;
  transition: all 0.2s ease;
  min-height: ${({ theme }) => theme.resolve("spacing_5xl")};
  box-shadow: ${({ isActive, theme }) =>
    isActive ? `0 1px 3px ${theme.resolve("color_neutral_gray300")}` : "none"};
  white-space: nowrap;

  svg {
    width: ${({ theme }) => theme.resolve("spacing_3xl")};
    height: ${({ theme }) => theme.resolve("spacing_3xl")};
  }

  &:hover {
    background: ${({ isActive, theme }) =>
      isActive
        ? theme.resolve("color_bg_surface_subtle")
        : theme.resolve("color_bg_surface_standard")};
  }

  &:focus {
    outline: none;
  }

  &:disabled {
    cursor: not-allowed;
  }

  ${Badge} {
    height: 16px;
    min-width: 16px;
    padding: 1px 3px;
    margin-left: ${({ theme }) => theme.resolve("spacing_md")};
    font-size: 11px;
  }
`;

type ToggleGroupProps<ButtonName extends string> = {
  names: ButtonName[];
  clickHandler: (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    buttonName: ButtonName
  ) => void;
  activeButton: ButtonName;
  icons?: JSX.Element[];
  style?: CSSProperties;
  /** Disables every button in the group. */
  disabled?: boolean;
  /** Disables only the named buttons (e.g. a segment with no items to show), leaving the rest clickable. */
  disabledButtons?: ButtonName[];
  badges?: (string | undefined)[];
};

export function ToggleGroup<ButtonName extends string>({
  names,
  clickHandler,
  activeButton,
  icons,
  style,
  disabled,
  disabledButtons,
  badges,
}: ToggleGroupProps<ButtonName>) {
  if (!names) {
    console.error(
      `<ToggleGroup/> requires an array of names to be rendered: ['Button1', 'Button2']`
    );
    return null;
  }
  if (activeButton === undefined) {
    console.error(
      `<ToggleGroup/> require an activeButton prop, this is a string that should match one of the names in names[]`
    );
    return null;
  }

  return (
    <ButtonGroup style={style}>
      {names.map((name, index) => {
        const isActive = name === activeButton;
        const isButtonDisabled = disabled || disabledButtons?.includes(name);
        return (
          <StyledButton
            key={index}
            data-name={name}
            isActive={isActive}
            onClick={(e) => {
              clickHandler(e, name);
            }}
            disabled={isButtonDisabled}
          >
            {icons?.[index]}
            <TextBaseMedium
              color={
                isButtonDisabled
                  ? "tertiary"
                  : isActive
                  ? "primary"
                  : "secondary"
              }
            >
              {name}
            </TextBaseMedium>
            {badges?.[index] && <Badge>{badges[index]}</Badge>}
          </StyledButton>
        );
      })}
    </ButtonGroup>
  );
}
